import { useMemo, useState } from "react";
import clsx from "clsx";
import type { JfSoilType } from "@tbm/shared";
import { buildSoilColourMap, soilColour } from "./soilColours";

export interface SoilRowDraft {
  /** Free-form description, e.g. "Fine sand with silt". Same string = same soil. */
  soilType: JfSoilType;
  unitWeightKnm3: number | "";
  startM: number | "";
  endM: number | "";
  sfSsKpaInit: number | "";
  cInit: number | "";
  aInit: number | "";
}

interface SoilsEditorProps {
  rows: SoilRowDraft[];
  driveLengthM: number | null;
  onChange: (rows: SoilRowDraft[]) => void;
}

/**
 * Fields that describe the *soil itself* (rather than where the row
 * sits along chainage). When two rows share a name these must agree
 * — the editor keeps them in sync automatically so the operator only
 * enters values once.
 */
const SHARED_FIELDS: readonly (keyof SoilRowDraft)[] = [
  "unitWeightKnm3",
  "sfSsKpaInit",
  "cInit",
  "aInit",
] as const;

function nameKey(s: string): string {
  return s.trim().toLowerCase();
}

export function SoilsEditor({ rows, driveLengthM, onChange }: SoilsEditorProps) {
  // Index of the row currently being dragged. The whole row is the
  // drag source; `onDragOver` on each row updates this so we can show
  // a drop indicator on the hovered target.
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const setCell = (i: number, key: keyof SoilRowDraft, value: string) => {
    const next = rows.slice();
    const current = next[i]!;

    if (key === "soilType") {
      // Renaming a row. If the new name matches an existing row's name
      // (case-insensitive), copy the shared params from that row so
      // the new row inherits its soil profile.
      const newName = value;
      const newKey = nameKey(newName);
      const donor =
        newKey.length > 0
          ? next.find((r, j) => j !== i && nameKey(r.soilType) === newKey)
          : undefined;
      if (donor) {
        next[i] = {
          ...current,
          soilType: newName,
          unitWeightKnm3: donor.unitWeightKnm3,
          sfSsKpaInit: donor.sfSsKpaInit,
          cInit: donor.cInit,
          aInit: donor.aInit,
        };
      } else {
        next[i] = { ...current, soilType: newName };
      }
    } else if (key === "startM" || key === "endM") {
      // Per-row geometry. Never propagated.
      const v = value === "" ? "" : Number(value);
      next[i] = { ...current, [key]: v as number | "" };
    } else if (SHARED_FIELDS.includes(key)) {
      // Soil-profile parameter. Propagate to every row sharing the
      // current row's name so same-named layers stay consistent. If
      // the current row has no name yet, treat as per-row.
      const v = value === "" ? "" : Number(value);
      const groupKey = nameKey(current.soilType);
      if (groupKey.length === 0) {
        next[i] = { ...current, [key]: v as number | "" };
      } else {
        for (let j = 0; j < next.length; j++) {
          if (nameKey(next[j]!.soilType) === groupKey) {
            next[j] = { ...next[j]!, [key]: v as number | "" };
          }
        }
      }
    } else {
      const v = value === "" ? "" : Number(value);
      next[i] = { ...current, [key]: v as number | "" };
    }
    onChange(next);
  };

  const addRow = () => {
    const last = rows[rows.length - 1];
    const start = last && typeof last.endM === "number" ? last.endM : 0;
    onChange([
      ...rows,
      {
        soilType: "",
        unitWeightKnm3: "",
        startM: start,
        endM: driveLengthM ?? "",
        sfSsKpaInit: "",
        cInit: "",
        aInit: "",
      },
    ]);
  };

  const delRow = (i: number) => {
    if (rows.length <= 1) return;
    onChange(rows.filter((_, idx) => idx !== i));
  };

  const moveRow = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) {
      return;
    }
    const next = rows.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    onChange(next);
  };

  // Datalist of distinct names suggested while typing — helps the
  // operator reuse an existing name (and therefore its shared params)
  // instead of typing a near-duplicate.
  const distinctDescriptions = Array.from(
    new Set(rows.map((r) => r.soilType.trim()).filter((s) => s.length > 0)),
  );

  // Count how many rows share each name — used to surface "linked"
  // hint on shared parameter cells.
  const countByKey = new Map<string, number>();
  for (const r of rows) {
    const k = nameKey(r.soilType);
    if (k.length === 0) continue;
    countByKey.set(k, (countByKey.get(k) ?? 0) + 1);
  }

  // Build a stable name→colour map once per render so every swatch
  // uses the same slot assignment. First-occurrence order matches
  // what the schematic preview and summary panel show.
  const colourMap = useMemo(
    () => buildSoilColourMap(rows.map((r) => ({ soilType: r.soilType }))),
    [rows],
  );

  return (
    <div className="inline-block rounded border border-border1 overflow-x-auto max-w-full">
      <datalist id="jf-soil-suggestions">
        {distinctDescriptions.map((d) => (
          <option key={d} value={d} />
        ))}
      </datalist>
      <table className="text-[11.5px]">
        <thead className="bg-surface2">
          <tr className="text-[10.5px] text-dim">
            <Th className="w-7" />
            <Th>Description</Th>
            <Th>Unit weight (kN/m³)</Th>
            <Th>Start (m)</Th>
            <Th>End (m)</Th>
            <Th>Skin friction (kPa)</Th>
            <Th>Model constant c</Th>
            <Th>Model constant a</Th>
            <Th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const key = nameKey(r.soilType);
            const groupSize = key.length > 0 ? countByKey.get(key) ?? 0 : 0;
            const linked = groupSize > 1;
            const colour = soilColour(r.soilType, colourMap);
            const isDragging = dragIdx === i;
            const isDropTarget = dragOverIdx === i && dragIdx !== null && dragIdx !== i;
            return (
              <tr
                key={i}
                draggable
                onDragStart={(e) => {
                  setDragIdx(i);
                  // text/plain payload makes Firefox happy.
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", String(i));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (dragOverIdx !== i) setDragOverIdx(i);
                }}
                onDragLeave={() => {
                  if (dragOverIdx === i) setDragOverIdx(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIdx !== null && dragIdx !== i) {
                    moveRow(dragIdx, i);
                  }
                  setDragIdx(null);
                  setDragOverIdx(null);
                }}
                onDragEnd={() => {
                  setDragIdx(null);
                  setDragOverIdx(null);
                }}
                className={clsx(
                  "border-t border-border1 transition-colors",
                  isDragging && "opacity-50",
                  isDropTarget && "bg-wb/10",
                )}
              >
                <Td>
                  {/* Drag handle. Whole row is the drag source (so the
                      user can grab the colour swatch too), but this
                      handle is the obvious affordance. */}
                  <span
                    aria-label="Drag to reorder"
                    title="Drag to reorder"
                    className="inline-block cursor-grab active:cursor-grabbing text-muted hover:text-text select-none px-1"
                  >
                    ⋮⋮
                  </span>
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    {/* Colour swatch — named after the soil, so two
                        rows with the same name share a colour. */}
                    <span
                      aria-hidden="true"
                      className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                      style={{ background: colour }}
                      title={
                        linked
                          ? `Linked: ${groupSize} rows share these parameters`
                          : undefined
                      }
                    />
                    <input
                      type="text"
                      value={r.soilType}
                      onChange={(e) => setCell(i, "soilType", e.target.value)}
                      placeholder="e.g. Fine sand with silt"
                      className="w-32 bg-bg border border-border1 rounded px-2 py-1 text-[11.5px] text-text focus:outline-none focus:border-wb"
                      list="jf-soil-suggestions"
                    />
                  </div>
                </Td>
                <Td>
                  <NumCell
                    value={r.unitWeightKnm3}
                    linked={linked}
                    onChange={(v) => setCell(i, "unitWeightKnm3", v)}
                  />
                </Td>
                <Td>
                  <NumCell value={r.startM} onChange={(v) => setCell(i, "startM", v)} />
                </Td>
                <Td>
                  <NumCell value={r.endM} onChange={(v) => setCell(i, "endM", v)} />
                </Td>
                <Td>
                  <NumCell
                    value={r.sfSsKpaInit}
                    linked={linked}
                    onChange={(v) => setCell(i, "sfSsKpaInit", v)}
                  />
                </Td>
                <Td>
                  <NumCell
                    value={r.cInit}
                    linked={linked}
                    onChange={(v) => setCell(i, "cInit", v)}
                  />
                </Td>
                <Td>
                  <NumCell
                    value={r.aInit}
                    linked={linked}
                    onChange={(v) => setCell(i, "aInit", v)}
                  />
                </Td>
                <Td>
                  <button
                    type="button"
                    onClick={() => delRow(i)}
                    disabled={rows.length <= 1}
                    className={clsx(
                      "text-[11px] px-2 py-1 rounded",
                      rows.length <= 1
                        ? "text-dim cursor-not-allowed"
                        : "text-muted hover:text-wb",
                    )}
                  >
                    ✕
                  </button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 border-t border-border1 bg-surface1 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={addRow}
          className="text-[11px] uppercase tracking-wider px-3 py-1 rounded border border-border2 text-text hover:border-wb hover:text-wb"
        >
          + Add soil layer
        </button>
        <span className="text-[10.5px] text-dim">
          Drag rows to reorder. Rows sharing a description share parameters and a colour.
        </span>
      </div>
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={clsx("text-left px-3 py-2 font-medium", className)}>
      {children}
    </th>
  );
}

function Td({ children }: { children?: React.ReactNode }) {
  return <td className="px-2 py-1.5">{children}</td>;
}

function NumCell({
  value,
  linked = false,
  onChange,
}: {
  value: number | "";
  /** True when this cell is one of a set of rows sharing the same
   *  soil name; we tint the border to make the link visible. */
  linked?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <input
      type="number"
      step="any"
      value={value === "" ? "" : value}
      onChange={(e) => onChange(e.target.value)}
      className={clsx(
        "w-full bg-bg border rounded px-2 py-1 text-[12px] text-text focus:outline-none focus:border-wb",
        linked ? "border-wb/40" : "border-border1",
      )}
      title={linked ? "Linked to other rows with the same description" : undefined}
    />
  );
}
